import { CommonModule, registerLocaleData } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import { Component, ElementRef, HostListener, LOCALE_ID, OnDestroy, OnInit } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { UiKitModule } from '@serviceplan/ui-kit';
// eslint-disable-next-line  @nx/enforce-module-boundaries
import { AssetGalleryViewComponent, AssetViewTypeService, GridConfig } from '@thehubz/client/assets-module';
import {
  AppSettings,
  AssetUIModel,
  ClientCoreModule,
  ClientNotificationHandlerService,
  CoreDownloadService,
  FilterAllOptions,
  ResizeService,
  ViewType,
} from '@thehubz/client/core';
import { Asset } from '@thehubz/client/data-access-api';
import { FileUploadService } from '@thehubz/client/file-upload';
import { AnyType, createAssetS3Key, createVersionString } from '@thehubz/shared/model';
import { Subscription, delay, map, tap, timeout } from 'rxjs';
import { HeaderComponent } from '../header/header.component';
import { JackpotInfo } from '../model/jackpot-data.model';
import { JackpotAssetsActionsComponent } from './components/jackpot-assets-actions/jackpot-assets-actions.component';
import { JackpotDownloadComponent } from './components/jackpot-download/jackpot-download.component';
import { JackpotModalComponent } from './components/jackpot-modal/jackpot-modal.component';
import { JackpotAssetsFilterService } from './services/jackpot-assets-filter.service';

const API_URL = '/api/jackpot-assets';

registerLocaleData(localeDe, 'de');

@Component({
  selector: 'wl-jackpot-assets',
  templateUrl: './jackpot-assets.component.html',
  styleUrls: ['./jackpot-assets.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    HttpClientModule,
    MatCardModule,
    ClientCoreModule,
    UiKitModule,
    HeaderComponent,
    AssetGalleryViewComponent,
    JackpotAssetsActionsComponent,
    JackpotDownloadComponent,
  ],
  providers: [{ provide: LOCALE_ID, useValue: 'de' }],
})
export class JackpotAssetsComponent implements OnInit, OnDestroy {
  assets: AssetUIModel[] = [];
  info?: JackpotInfo;
  imageObject: AssetUIModel[] = [];
  currentTabAssets: AssetUIModel[] = [];
  imageSplitObject!: AssetUIModel[][];
  sortByNew = true;
  selectedViewSection2: ViewType = 'sortByLastCreated';

  currentSize: AnyType;
  prefix = 'is-';
  size = 4;
  sizes = AppSettings.SCREEN_SIZES;
  itemSize = 310;
  offset = 0;

  gridConfig: GridConfig = {
    showGridView: true,
    showGridViewSmall: true,
    showGridViewLarge: false,
    gridSize: 4,
  };

  filterObject: FilterAllOptions = {
    filters: {},
    sort: {},
    limit: 50,
    skip: 0,
  };

  configObject = {
    callFrom: 'jackpotAssets',
    hideDelete: true,
    hideAddToBasket: true,
    hideAddToCollection: true,
    emitSelectedAssets: false,
    hideListViewMenu: true,
    showIconViewMenu: true,
    showPreviewButton: true,
    showMetaData: true,
  };
  gridSettingsKeySuffix = 'AssetOverview';
  selectedViewType!: ViewType;
  selectedTabKey = 'all_assets';
  searchText = '';
  appliedFilters = 0;

  private resizeSubscription?: Subscription;

  constructor(
    private activatedRoute: ActivatedRoute,
    private httpClient: HttpClient,
    private coreDownloadService: CoreDownloadService,
    private dialog: MatDialog,
    private resizeSvc: ResizeService,
    private elementRef: ElementRef,
    private assetViewTypeService: AssetViewTypeService,
    private jackpotAssetFilterService: JackpotAssetsFilterService,
    private clientNotificationHandlerService: ClientNotificationHandlerService,
  ) {
    this.initAssetView();
    this.resizeSubscription = this.resizeSvc.onResize$.pipe(delay(0)).subscribe((x) => {
      const currentSize = this.sizes.find((x) => {
        const el = this.elementRef.nativeElement.querySelector(`.${this.prefix}${x.id}`);
        const isVisible = window.getComputedStyle(el).display != 'none';
        return isVisible;
      });
      this.currentSize = currentSize;
      this.setAssetView(currentSize);
    });
  }

  @HostListener('window:resize', [])
  private onResize() {
    this.detectScreenSize();
  }

  private detectScreenSize() {
    const currentSize = this.sizes.find((x) => {
      const el = this.elementRef.nativeElement.querySelector(`.${this.prefix}${x.id}`);
      const isVisible = window.getComputedStyle(el).display != 'none';
      return isVisible;
    });
    this.resizeSvc.onResize(currentSize?.id);
  }

  ngOnInit(): void {
    this.fetchAssets();
  }

  fetchAssets() {
    const query = this.activatedRoute.snapshot.queryParamMap;

    let testParams = '';
    if (query.has('test')) {
      const tp = {
        r1: query.get('r1'),
        r2: query.get('r2'),
        dd: query.get('dd'),
      };
      testParams = JSON.stringify(tp);
    }

    this.httpClient
      .get<{ assets: Asset[]; info: JackpotInfo }>(API_URL, { params: { ts: Date.now(), testParams } })
      .pipe(
        tap((res) => (this.info = res.info)),
        map((res) => this.generatePreview(this.jackpotAssetFilterService.splitAssets(res.assets) ?? [])),
      )
      .subscribe((data) => {
        this.assets = data;
        this.imageObject = data;
        this.currentTabAssets = data;
        this.imageSplitObject = this.split(this.imageObject, this.size);
      });
  }

  downloadFormat(asset: AssetUIModel) {
    const dialogRef = this.dialog.open(JackpotDownloadComponent, { width: '100%', maxWidth: '60rem', data: { assets: [asset], info: this.info } });
    dialogRef.afterClosed().subscribe((data) => {
      if (data) {
        const files = this.listAllFiles(asset);
        const file = files.filter((file) => file.name === asset.name)[0];
        this.downloadFile(file.key, file.name);
      }
    });
  }

  downloadFile(key: string, name: string) {
    this.httpClient
      .get<{ presignedUrl: string }>(`${API_URL}/download-file/${key}`)
      .pipe(timeout(60000))
      .subscribe((res) => {
        this.coreDownloadService.downloadSignedUrl(res.presignedUrl, name);
      });
  }

  listAllFiles(asset: AssetUIModel) {
    const versionString = createVersionString(asset.mainFile.version);
    const files: { key: string; name: string }[] = [];
    asset.currentVersion?.aspectRatios?.forEach((aspectRatio) => {
      aspectRatio.formats?.forEach((format) => {
        if (format?.fileNameRef && !format.fileNameRef.startsWith('gen') && format.fileNameRef !== 'main') {
          const key = createAssetS3Key(asset._id, versionString, aspectRatio.aspectRatio, format.fileNameRef + '.' + format.ext);
          files.push({ key, name: format.name });
        }
      });
    });
    return files;
  }

  private generatePreview(assets: Asset[]): AssetUIModel[] {
    return assets.map((asset) => {
      return {
        ...asset,
        imageUrl: asset.assetType === 'Audio' ? '' : this.getThumbnailUrl(asset),
        previewUrl: this.getPreviewUrl(asset),
      };
    });
  }

  private getThumbnailUrl(asset: Asset, overrideName?: string, version?: number) {
    if (asset.assetType === Asset.AssetTypeEnum.Video) {
      return API_URL + '/thumbnail-url/' + FileUploadService.createAssetFileKey(asset, overrideName || 'gen-thumbnail.0000000.jpg', false, version);
    }
    return API_URL + '/thumbnail-url/' + FileUploadService.createAssetFileKey(asset, overrideName || 'gen-thumbnail.webp', false, version);
  }

  private getPreviewUrl(asset: Asset, version?: number) {
    if (asset.assetType === Asset.AssetTypeEnum.Video) {
      return API_URL + '/thumbnail-url/' + FileUploadService.createAssetFileKey(asset, 'gen-preview.mp4', false, version);
    }
    return API_URL + '/thumbnail-url/' + FileUploadService.createAssetFileKey(asset, 'gen-preview.mp3', false, version);
  }

  private split(input: AssetUIModel[], groupSize: number): AssetUIModel[][] {
    const out = [];
    for (let i = 0; i < input.length; i += groupSize) {
      out.push(input.slice(i, i + groupSize));
    }
    return out;
  }

  filterAssets(filterKey?: string, filterData?: any) {
    let assets: AssetUIModel[] = [];

    if (filterKey) {
      switch (filterKey) {
        case 'search': {
          assets = [...this.imageObject];
          this.imageObject = this.jackpotAssetFilterService.applySingleFilter('searchText', filterKey, assets, this.searchText);
          break;
        }
        case 'assetType': {
          assets = [...this.assets];
          this.imageObject = this.jackpotAssetFilterService.applySingleFilter(filterKey, this.selectedTabKey, assets, this.searchText);
          this.currentTabAssets = [...this.imageObject];
          break;
        }
        default: {
          assets = [...this.assets];
          this.imageObject = this.jackpotAssetFilterService.applySingleFilter('default', filterKey, assets);
          break;
        }
      }
    } else if (filterData) {
      const filteredData = this.jackpotAssetFilterService.applyMultipleFilters(filterData, [...this.currentTabAssets]);
      this.imageObject = filteredData.filteredAssets;
      this.appliedFilters = filteredData.appliedFilters;
    } else {
      this.imageObject = [...this.assets];
      this.appliedFilters = 0;
    }
    this.imageSplitObject = this.split(this.imageObject, this.size);
  }

  openAssetPreview(asset: AssetUIModel) {
    if (asset.previewUrl) {
      this.httpClient
        .get<{ presignedUrl: string }>(asset.previewUrl)
        .pipe(timeout(60000))
        .subscribe({
          next: (res) => {
            if (res) {
              this.dialog.open(JackpotModalComponent, {
                minHeight: asset.assetType === 'Video' ? '40rem' : 'auto',
                maxWidth: asset.assetType === 'Video' ? '70rem' : '40rem',
                width: '100%',

                data: {
                  type: asset.assetType,
                  header: asset.name,
                  previewUrl: res.presignedUrl,
                  imageUrl: asset.imageUrl,
                  showFooter: false,
                },
              });
            }
          },
          error: () => {
            this.clientNotificationHandlerService.errorStringNotification('Asset Vorschau ist nicht verfügbar');
          },
        });
    }
  }

  toggleFilterTabs(tabKey: string) {
    if (tabKey !== this.selectedTabKey) {
      this.selectedTabKey = tabKey;
      if (tabKey !== 'all_assets') {
        this.filterAssets('assetType');
      } else {
        this.fetchAssets();
      }
    }
  }

  sortBy(event: ViewType) {
    this.sortByNew = event === 'sortByLastCreated';
    this.selectedViewSection2 = event || 'sortByLastCreated';
    this.filterAssets(event);
  }

  searchTextChanged(searchText: string) {
    this.searchText = searchText;
    if (searchText) {
      this.filterAssets('search');
    } else {
      this.filterAssets('assetType');
    }
  }

  clearSearch(searchText: string) {
    this.searchText = searchText;
    this.imageObject = this.currentTabAssets;
    this.imageSplitObject = this.split(this.imageObject, this.size);
  }

  initAssetView() {
    const gridSettings = this.assetViewTypeService.getGridConfig(this.gridSettingsKeySuffix);

    if (Object.keys(gridSettings).length) {
      this.gridConfig = gridSettings;
      return;
    }
    this.assetViewTypeService.setGridConfig(this.gridSettingsKeySuffix, this.gridConfig);
  }

  setAssetView(currentSize?: AnyType) {
    const gridSettings = this.assetViewTypeService.getGridConfig(this.gridSettingsKeySuffix);
    if (gridSettings.showGridViewLarge) {
      this.size = 8;
      if (currentSize?.name === 'xl') this.size = 8;
      if (currentSize?.name === 'lg') this.size = 8;
      if (currentSize?.name === 'md') this.size = 4;
    }
    if (gridSettings.showGridViewSmall) {
      this.size = 4;
      if (currentSize?.name === 'lg') this.size = 3;
      if (currentSize?.name === 'md') this.size = 2;
    }
  }

  changeAssetView(view: AnyType) {
    if (view === 'grid_4') {
      this.gridConfig.showGridViewSmall = true;
      this.gridConfig.showGridViewLarge = false;
      this.selectedViewType = 'grid_4';
      this.size = 4;
    }
    if (view === 'grid_8') {
      this.gridConfig.showGridViewSmall = false;
      this.gridConfig.showGridViewLarge = true;
      this.selectedViewType = 'grid_8';
      this.size = 8;
      this.itemSize = 155;
    }
    this.imageSplitObject = this.split(this.imageObject, this.size);
    this.assetViewTypeService.setGridConfig(this.gridSettingsKeySuffix, this.gridConfig);
  }

  ngOnDestroy(): void {
    this.resizeSubscription?.unsubscribe();
  }
}
